// Presentation vocabulary for audit readiness (APP-004 R3 UI-P7).
//
// Two things must never blur here. A finding that is resolved or accepted is
// finished, and nothing reopens it: a recurrence is raised as a new finding,
// so no wording anywhere offers to undo one. And a readiness component with
// nothing to measure did not fail; it did not apply, and the backend moved its
// weight elsewhere. Both readings live in this module so no screen invents its
// own.
import { Badge } from "@odingard/design-system";
import type { BadgeTone } from "@odingard/design-system";
import type {
  AuditProjectStatus,
  AuditScopeType,
  FindingSeverity,
  FindingStatus,
  ReadinessComponent,
} from "@odingard/sdk-client";

const PROJECT_STATUS: Record<AuditProjectStatus, { label: string; tone: BadgeTone }> = {
  planning: { label: "Planning", tone: "neutral" },
  fieldwork: { label: "Fieldwork", tone: "info" },
  remediation: { label: "Remediation", tone: "warning" },
  closed: { label: "Closed", tone: "success" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

export function ProjectStatusBadge({ status }: { status: AuditProjectStatus }) {
  const it = PROJECT_STATUS[status];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

export function projectStatusLabel(status: AuditProjectStatus): string {
  return PROJECT_STATUS[status].label;
}

export const PROJECT_STATUSES: AuditProjectStatus[] = [
  "planning",
  "fieldwork",
  "remediation",
  "closed",
  "cancelled",
];

/**
 * The backend lifecycle, mirrored only to decide which buttons to draw. The
 * backend still refuses anything it will not allow, and its answer is what the
 * user sees.
 */
export const NEXT_PROJECT_STATUS: Record<AuditProjectStatus, AuditProjectStatus[]> = {
  planning: ["fieldwork", "cancelled"],
  fieldwork: ["remediation", "closed", "cancelled"],
  remediation: ["closed", "cancelled"],
  closed: [],
  cancelled: [],
};

const FINDING_STATUS: Record<FindingStatus, { label: string; tone: BadgeTone }> = {
  open: { label: "Open", tone: "danger" },
  in_remediation: { label: "In remediation", tone: "warning" },
  resolved: { label: "Resolved", tone: "success" },
  accepted: { label: "Accepted", tone: "info" },
  disputed: { label: "Disputed", tone: "neutral" },
};

export function FindingStatusBadge({ status }: { status: FindingStatus }) {
  const it = FINDING_STATUS[status];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

export function findingStatusLabel(status: FindingStatus): string {
  return FINDING_STATUS[status].label;
}

export const FINDING_STATUSES: FindingStatus[] = [
  "open",
  "in_remediation",
  "resolved",
  "accepted",
  "disputed",
];

/** Terminal states have no onward transitions, and there is no reopen route. */
export const NEXT_FINDING_STATUS: Record<FindingStatus, FindingStatus[]> = {
  open: ["in_remediation", "accepted", "disputed"],
  in_remediation: ["resolved", "accepted", "disputed"],
  disputed: ["open", "in_remediation"],
  resolved: [],
  accepted: [],
};

export function isFindingClosed(status: FindingStatus): boolean {
  return status === "resolved" || status === "accepted";
}

const SEVERITY: Record<FindingSeverity, { label: string; tone: BadgeTone }> = {
  low: { label: "Low", tone: "neutral" },
  medium: { label: "Medium", tone: "info" },
  high: { label: "High", tone: "warning" },
  critical: { label: "Critical", tone: "danger" },
};

export function SeverityBadge({ severity }: { severity: FindingSeverity }) {
  const it = SEVERITY[severity];
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

export function severityLabel(severity: FindingSeverity): string {
  return SEVERITY[severity].label;
}

export const SEVERITIES: FindingSeverity[] = ["low", "medium", "high", "critical"];

const SCOPE_TYPE: Record<AuditScopeType, string> = {
  framework_version: "Framework version",
  requirement: "Requirement",
  business_unit: "Business unit",
  system: "System",
};

export function scopeTypeLabel(type: AuditScopeType): string {
  return SCOPE_TYPE[type];
}

export const SCOPE_TYPES: AuditScopeType[] = [
  "framework_version",
  "requirement",
  "business_unit",
  "system",
];

/** True for the references Studio holds no registry for and validates nothing about. */
export function isFreeReference(type: AuditScopeType): boolean {
  return type === "business_unit" || type === "system";
}

/** Whole percentages read plainly; a redistributed weight needs its decimals. */
export function percent(value: number, places = 1): string {
  const scaled = value * 100;
  return `${Number.isInteger(scaled) ? scaled : scaled.toFixed(places)}%`;
}

/**
 * A component with a zero denominator had nothing in scope to measure. It is
 * omitted rather than scored, and the backend gave its weight to the
 * components that did apply.
 */
export function componentRatioLabel(component: ReadinessComponent): string {
  if (!component.included) return "Not applicable";
  return percent(component.ratio ?? 0);
}
